/**
 * Routing rule editor — form type, templates, mappers, and default values.
 */

import { ArrowRight, ArrowLeft, Phone, Clock, Settings, type LucideIcon } from 'lucide-react';
import type { Sip } from '@sbclite/core';
import {
  type RoutingRuleCreateInputStrict,
  type NumberPatternType,
  regexToPattern,
} from '@sbclite/core/schemas';

/** Form data type from shared schema. */
export type RoutingRuleFormData = RoutingRuleCreateInputStrict;

interface RoutingTemplate {
  name: string;
  icon: LucideIcon;
  description: string;
  template: {
    sourceScope: 'any' | 'sip_peer' | 'peer_group';
    destinationType: 'peer' | 'peerGroup' | 'reject';
    destinationTarget: string;
    priority: number;
    recordRouteEnabled: boolean;
    decrementMaxForwards: boolean;
    insertViaHeader: boolean;
    numberPattern?: {
      called?: { type: NumberPatternType; value: string };
      calling?: { type: NumberPatternType; value: string };
    };
    conditions?: {
      timeWindow?: {
        start: string;
        end: string;
        timezone: string;
      };
    };
    actions?: {
      enforceCodecs?: string[];
    };
  };
}

export const ROUTING_TEMPLATES: Record<string, RoutingTemplate> = {
  blank: {
    name: 'Start from Scratch',
    icon: Settings,
      description: 'Create a custom routing rule with default settings',
      template: {
      sourceScope: 'any',
      destinationType: 'peer',
      destinationTarget: '',
      priority: 50,
      recordRouteEnabled: true,
      decrementMaxForwards: true,
      insertViaHeader: true
    }
  },
  inbound: {
    name: 'Inbound Call Routing',
    icon: ArrowRight,
      description: 'Route incoming calls from carriers to internal systems',
      template: {
      sourceScope: 'any',
      destinationType: 'peer',
      destinationTarget: '',
      priority: 10,
      recordRouteEnabled: true,
      decrementMaxForwards: true,
      insertViaHeader: true,
      numberPattern: {
        called: { type: 'prefix', value: '+1' }
      }
    }
  },
  outbound: {
    name: 'Outbound Call Routing',
    icon: ArrowLeft,
      description: 'Route outgoing calls from internal systems to carriers',
      template: {
      sourceScope: 'any',
      destinationType: 'peer',
      destinationTarget: '',
      priority: 20,
      recordRouteEnabled: true,
      decrementMaxForwards: true,
      insertViaHeader: true,
      numberPattern: {
        calling: { type: 'prefix', value: '1' }
      }
    }
  },
  teams: {
    name: 'Microsoft Teams Routing',
    icon: Phone,
      description: 'Direct routing for Microsoft Teams integration',
      template: {
      sourceScope: 'sip_peer',
      destinationType: 'peer',
      destinationTarget: '',
      priority: 5,
      recordRouteEnabled: true,
      decrementMaxForwards: true,
      insertViaHeader: true,
      actions: {
        enforceCodecs: ['SILK', 'G.722', 'PCMU']
      }
    }
  },
  failover: {
    name: 'Failover Routing',
    icon: Settings,
      description: 'Backup routing when primary routes fail',
      template: {
      sourceScope: 'any',
      destinationType: 'peerGroup',
      destinationTarget: '',
      priority: 100,
      recordRouteEnabled: true,
      decrementMaxForwards: true,
      insertViaHeader: true
    }
  },
  business_hours: {
    name: 'Business Hours Routing',
    icon: Clock,
      description: 'Time-based routing for business hours',
      template: {
      sourceScope: 'any',
      destinationType: 'peer',
      destinationTarget: '',
      priority: 15,
      recordRouteEnabled: true,
      decrementMaxForwards: true,
      insertViaHeader: true,
      conditions: {
        timeWindow: {
          start: '09:00',
          end: '17:00',
          timezone: 'America/New_York'
        }
      }
    }
  }
};

export type TemplateKey = string;

/**
 * Result of mapping a database destination type to the UI editor.
 *
 * The console editor only supports `peer`, `peerGroup`, and `reject`
 * destinations. Production rules may use other runtime destination types
 * (e.g. `gateway`, `pstn`, `forward`) that the console cannot edit. Rather
 * than silently coercing those to `peer` (which corrupts the rule on save),
 * we surface them as unsupported so the editor can warn the user.
 */
export type DbDestinationTypeMapping =
  | { supported: true; uiType: 'peer' | 'peerGroup' | 'reject' }
  | { supported: false; rawType: string };

/**
 * Map a database destination type to the UI editor value.
 *
 * Supported types map to the editor enum. Unknown/unsupported types return
 * `{ supported: false, rawType }` so callers can warn and preserve the
 * original value instead of silently coercing it.
 */
export const mapDbDestinationTypeToUi = (dbType: string | undefined): DbDestinationTypeMapping => {
  switch (dbType) {
    case 'sip_peer':
    case 'peer':
      return { supported: true, uiType: 'peer' };
    case 'peer_group':
    case 'peerGroup':
      return { supported: true, uiType: 'peerGroup' };
    case 'reject':
      return { supported: true, uiType: 'reject' };
    default:
      return { supported: false, rawType: dbType ?? '' };
  }
};

/** Map UI destination types to database values. */
export const mapUiDestinationTypeToDb = (uiType: string): string => {
  switch (uiType) {
    case 'peer':
      return 'sip_peer';
    case 'peerGroup':
      return 'peer_group';
    case 'reject':
      return 'reject';
    default:
      return 'sip_peer';
  }
};

/**
 * If `rule` uses a destination type the console editor cannot edit, return the
 * raw destination type string; otherwise return `null`.
 */
export function getUnsupportedDestinationType(rule: Sip.RoutingRuleInfo): string | null {
  const mapping = mapDbDestinationTypeToUi(rule.destinationType);
  return mapping.supported ? null : mapping.rawType;
}

/**
 * Original destination fields to preserve verbatim on save when a rule uses an
 * unsupported destination type. The editor cannot edit these, so it must not
 * rewrite them.
 */
export interface DestinationPreservation {
  destinationType: string;
  destinationTarget: string;
  peerId: string | null;
  peerGroupId: string | null;
}

/** Blank create defaults. */
function blankDefaults(): RoutingRuleFormData {
  return {
    name: '',
    description: '',
    enabled: true,
    priority: 50,
    hostId: null,
    sourceScope: 'any',
    sourcePeerId: null,
    sourcePeerGroupId: null,
    numberPattern: {
      called: { type: 'any', value: '' },
      calling: { type: 'any', value: '' }
    },
    destinationType: 'peer',
    destinationTarget: '',
    conditions: {},
    actions: {},
    inboundManipulationId: null,
    outboundManipulationId: null,
    recordRouteEnabled: true,
    decrementMaxForwards: true,
    insertViaHeader: true,
    routeHeaders: [],
    rfcConditions: {}
  };
}

/** Edit defaults derived from an existing rule. */
function editDefaults(rule: Sip.RoutingRuleInfo): RoutingRuleFormData {
  const numberPattern = rule.numberPattern || {
    called: rule.conditions?.toPattern
      ? regexToPattern(rule.conditions.toPattern)
      : { type: 'any' as const, value: '' },
    calling: rule.conditions?.fromPattern
      ? regexToPattern(rule.conditions.fromPattern)
      : { type: 'any' as const, value: '' }
  };

  // The editor form schema only accepts peer/peerGroup/reject. For an
  // unsupported runtime destination type (e.g. gateway/pstn/forward) we use
  // 'peer' as a placeholder that the user cannot edit; the original
  // destination is preserved verbatim on save via DestinationPreservation.
  const destMapping = mapDbDestinationTypeToUi(rule.destinationType);

  return {
    name: rule.name,
    description: rule.description || '',
    enabled: rule.enabled,
    priority: rule.priority,
    hostId: rule.hostId || null,
    sourceScope: rule.sourceScope ?? (rule.sourcePeerId ? 'sip_peer' : rule.sourcePeerGroupId ? 'peer_group' : 'any'),
    sourcePeerId: rule.sourcePeerId || null,
    sourcePeerGroupId: rule.sourcePeerGroupId || null,
    numberPattern,
    destinationType: destMapping.supported ? destMapping.uiType : 'peer',
    destinationTarget: rule.destinationTarget || '',
    conditions: rule.conditions || {},
    actions: rule.actions || {},
    inboundManipulationId: rule.inboundManipulationId || null,
    outboundManipulationId: rule.outboundManipulationId || null,
    recordRouteEnabled: rule.recordRouteEnabled !== false,
    decrementMaxForwards: rule.decrementMaxForwards !== false,
    insertViaHeader: rule.insertViaHeader !== false,
    routeHeaders: rule.routeHeaders || [],
    rfcConditions: rule.rfcConditions || {}
  };
}

/** Create defaults seeded from a template. */
function templateDefaults(key: TemplateKey): RoutingRuleFormData {
  const template = ROUTING_TEMPLATES[key];
  return {
    ...blankDefaults(),
    description: template.description,
    priority: template.template.priority,
    sourceScope: template.template.sourceScope,
    numberPattern: template.template.numberPattern || {
      called: { type: 'any', value: '' },
      calling: { type: 'any', value: '' }
    },
    destinationType: template.template.destinationType,
    destinationTarget: template.template.destinationTarget,
    conditions: template.template.conditions || {},
    actions: template.template.actions || {},
    recordRouteEnabled: template.template.recordRouteEnabled,
    decrementMaxForwards: template.template.decrementMaxForwards,
    insertViaHeader: template.template.insertViaHeader,
  };
}

/**
 * Build the RHF default values for the editor.
 * - editingRule set → edit defaults
 * - templateKey set → template-seeded create defaults
 * - otherwise → blank create defaults
 */
export function buildDefaultValues(opts: {
  editingRule?: Sip.RoutingRuleInfo | null;
  templateKey?: TemplateKey | null;
}): RoutingRuleFormData {
  if (opts.editingRule) return editDefaults(opts.editingRule);
  if (opts.templateKey) return templateDefaults(opts.templateKey);
  return blankDefaults();
}

/**
 * Transform form data into the API create/update payload.
 *
 * When `preserve` is provided (for rules whose destination type the console
 * cannot edit), the original destination fields are passed through verbatim
 * instead of being derived from the placeholder form values, so saving the
 * rule never corrupts the destination.
 */
export function toApiData(data: RoutingRuleFormData, preserve?: DestinationPreservation | null) {
  void preserve;
  const destinationType = data.destinationType;
  const destinationTarget = data.destinationTarget;

  return {
    name: data.name,
    description: data.description,
    enabled: data.enabled,
    priority: data.priority,
    hostId: data.hostId || null,
    sourceScope: data.sourceScope,
    sourcePeerId: data.sourcePeerId || null,
    sourcePeerGroupId: data.sourcePeerGroupId || null,
    ...(data.numberPattern ? { numberPattern: data.numberPattern } : {}),
    destinationType,
    destinationTarget,
    conditions: data.conditions,
    actions: data.actions,
    inboundManipulationId: data.inboundManipulationId || null,
    outboundManipulationId: data.outboundManipulationId || null,
    recordRouteEnabled: data.recordRouteEnabled,
    decrementMaxForwards: data.decrementMaxForwards,
    insertViaHeader: data.insertViaHeader,
    routeHeaders: data.routeHeaders,
    rfcConditions: data.rfcConditions
  };
}
